import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconButton, Stack, Tooltip } from '@mui/material';
import Slider from '@mui/material/Slider';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import { useHotkeys } from 'react-hotkeys-hook';

import { KeyboardShortcuts } from '@/shared/constants/keyboardShortcuts';

import { ShortcutTooltip } from './PlayerControls';

const PRIMARY_COLOR = '#ffffff';
const SECONDARY_COLOR = '#b8b8b8';

export const AudioPlayerControl = ({
  children,
  color = PRIMARY_COLOR,
  disabled = false,
  onClick,
  title,
}: any) => (
    <Tooltip title={title} arrow placement="top" slotProps={{ popper: { disablePortal: true } }}>
      <IconButton size="small" onClick={onClick} sx={{ color, mr: 1 }} disabled={disabled}>
        {children}
      </IconButton>
    </Tooltip>
);


type AudioControlsProps = {
  onVolumeChange?: (volume: number) => void;
  onMuteToggle?: (isMuted: boolean) => void;
  initialVolume?: number;
  initialMuted?: boolean;
};

const AudioControls: React.FC<AudioControlsProps> = ({
  onVolumeChange,
  onMuteToggle,
  initialVolume,
  initialMuted = true,
}) => {
  const [volume, setVolume] = useState<number>(initialVolume);
  const [isMuted, setIsMuted] = useState<boolean>(initialMuted);
  const [showSlider, setShowSlider] = useState<boolean>(false);

  const handleVolumeChange = useCallback(
    (_event: Event, newValue: number | number[]) => {
      const newVolume = newValue as number;
      setVolume(newVolume);

      if (newVolume === 0) {
        setIsMuted(true);
        onMuteToggle?.(true);
      } else if (isMuted) {
        setIsMuted(false);
        onMuteToggle?.(false);
      }

      onVolumeChange?.(newVolume);
    },
    [isMuted, onMuteToggle, onVolumeChange],
  );

  const handleMuteToggle = useCallback(() => {
    const newMutedState = !isMuted;

    if (newMutedState)
      onVolumeChange?.(0); // Set volume to current value when unmuting
    else {
      onVolumeChange?.(volume); // Set volume to 0 when muting
    }
    setIsMuted(newMutedState);
    onMuteToggle?.(newMutedState);
  }, [isMuted, onVolumeChange, volume, onMuteToggle]);

  // Mirror the usePlayerHotkeys pattern (J/K/L/T): keep a ref to the latest
  // handler so the hotkey registration stays stable across re-renders.
  const handleMuteToggleRef = useRef(handleMuteToggle);
  useEffect(() => {
    handleMuteToggleRef.current = handleMuteToggle;
  }, [handleMuteToggle]);

  useHotkeys(
    KeyboardShortcuts.Mute.hotkey,
    (e) => {
      e.preventDefault();
      handleMuteToggleRef.current();
    },
    {
      enableOnFormTags: false,
      enableOnContentEditable: false,
      preventDefault: true,
    },
  );

  const VolumeIcon = () => {
    if (isMuted || volume === 0) return <VolumeOffIcon />;
    if (volume < 50) return <VolumeDownIcon />;
    return <VolumeUpIcon />;
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      onMouseEnter={() => setShowSlider(true)}
      onMouseLeave={() => setShowSlider(false)}
      sx={{
        alignItems: 'center',
        backgroundColor: showSlider ? '#09090C38' : 'transparent',
        borderRadius: '24px',
        padding: showSlider ? '4px 12px 4px 4px' : '4px',
        transition: 'background-color 0.3s ease, padding 0.3s ease',
      }}
    >
      <AudioPlayerControl
        title={<ShortcutTooltip shortcut={KeyboardShortcuts.Mute} />}
        onClick={handleMuteToggle}
        color={isMuted ? SECONDARY_COLOR : PRIMARY_COLOR}
      >
        <VolumeIcon />
      </AudioPlayerControl>
      <Slider
        size="small"
        value={isMuted ? 0 : volume}
        onChange={handleVolumeChange}
        aria-label="Volume"
        min={0}
        max={100}
        sx={{
          width: showSlider ? 100 : 0,
          opacity: showSlider ? 1 : 0,
          transition: 'width 0.3s ease, opacity 0.3s ease',
          color: '#ffffff',
          padding: '13px 0', // Vertical padding to contain thumb
          '& .MuiSlider-rail': {
            opacity: 0.3,
          },
          '& .MuiSlider-track': {
            border: 'none',
          },
          '& .MuiSlider-thumb': {
            width: 12,
            height: 12,
            backgroundColor: '#fff',
            // Remove the orb/ripple effect on hover and focus
            '&:hover, &.Mui-focusVisible': {
              boxShadow: 'none',
            },
            '&.Mui-active': {
              boxShadow: 'none',
            },
            // Remove the before pseudo-element that creates the larger circle
            '&::before': {
              display: 'none',
            },
          },
        }}
      />
    </Stack>
  );
};

export default AudioControls;
